import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDebounceValue } from "usehooks-ts";

import { SearchServiceSort, SortDirection } from "../../../api/hooks";

export type InfoPageTemplateQueryStateProps<FilterType> = {
	defaultSortName: string;
	defaultSortDirection?: SortDirection;
	defaultSearchValue?: string;
	defaultPageNumber?: number;
	fallbackFilters?: Partial<FilterType>;
	replaceRoute?: boolean;
	goToDefaultPageOnFilterChange?: boolean;
	replaceRouteOnPageChange?: boolean;
	normalizeFilters?: (filters: Partial<FilterType>) => Partial<FilterType>;
};

const useInfoPageTemplateQueryState = <FilterType,>({
	defaultSortName,
	defaultSortDirection = "desc",
	defaultSearchValue = "",
	defaultPageNumber = 1,
	replaceRoute = true,
	goToDefaultPageOnFilterChange = true,
	replaceRouteOnPageChange = false,
	fallbackFilters = {},
	normalizeFilters,
}: InfoPageTemplateQueryStateProps<FilterType>) => {
	const [queryParams, setQueryParams] = useSearchParams();

	const page = Number(queryParams.get("pageNumber")) || defaultPageNumber;
	const sort = queryParams.get("name") || defaultSortName;
	const direction = queryParams.get("direction") || defaultSortDirection;
	const currentSort = { name: sort, direction } as SearchServiceSort;
	const currentSearchTerm = queryParams.get("search") || defaultSearchValue;

	const [isMounted, setIsMounted] = useState(false);
	const [debouncedSearchValue] = useDebounceValue(currentSearchTerm, 500);
	const parsedFilters = queryParams.get("filters")
		? (JSON.parse(queryParams.get("filters") || "{}") as FilterType)
		: fallbackFilters;
	const currentFilters = normalizeFilters ? normalizeFilters(parsedFilters) : parsedFilters;

	if (Object.keys(currentFilters).length > 0) {
		//convert all date strings to date objects
		// we can find dates by looking for the string "date-" at the beginning of the value
		for (const key in currentFilters) {
			if (typeof currentFilters[key] === "string" && currentFilters[key].startsWith("date-")) {
				currentFilters[key] = new Date(currentFilters[key].replace("date-", "")) as any;
			}
		}
	}

	useEffect(() => {
		if (isMounted && goToDefaultPageOnFilterChange) {
			handleQueryChange({ pageNumber: defaultPageNumber });
		}
	}, [JSON.stringify(currentFilters), debouncedSearchValue]);

	useEffect(() => {
		setIsMounted(true);
	}, []);

	useEffect(() => {
		handleQueryChange({ search: debouncedSearchValue });
	}, [debouncedSearchValue]);

	const handleQueryChange = (newParams, replaceRouteInstance = true) => {
		const newQueryParams = queryParams;
		Object.keys(newParams).forEach((key) => {
			if (newParams[key] === null || newParams[key] === undefined || newParams[key] === "") {
				newQueryParams.delete(key);
			} else {
				newQueryParams.set(key, newParams[key]);
			}
		});
		//replace the route unless explicitly told not to or we changed the page number
		setQueryParams(newQueryParams, { replace: replaceRoute && replaceRouteInstance, preventScrollReset: true });
	};

	const handleFilterChange = (newFilters: Partial<FilterType>) => {
		const convertedFilters = Object.keys(newFilters || {}).reduce((acc, key) => {
			if (newFilters[key] instanceof Date) {
				acc[key] = "date-" + newFilters[key].toISOString();
			} else {
				acc[key] = newFilters[key];
			}
			return acc;
		}, {});
		handleQueryChange({ filters: JSON.stringify({ ...convertedFilters }) });
	};

	return {
		pageNumber: page,
		setPageNumber: (newPage: number) => handleQueryChange({ pageNumber: newPage }, replaceRouteOnPageChange),
		sort: currentSort,
		setSort: (newSort: SearchServiceSort) =>
			handleQueryChange({ name: newSort.name, direction: newSort.direction }),
		filters: currentFilters || {},
		hasExplicitFilters: !!queryParams.get("filters"),
		setFilters: (newFilters: Partial<FilterType>) => handleFilterChange(newFilters),
		handleQueryChange,
		searchValue: currentSearchTerm,
		debouncedSearchValue,
		setDebouncedSearch: (newSearch: string) => handleQueryChange({ search: newSearch }, replaceRouteOnPageChange),
	};
};

export default useInfoPageTemplateQueryState;
